import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  Modal,
  HeadingText,
  StackLayout,
  ParagraphText,
  Box,
  Button,
  Divider,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  AuditLogRow,
  TreeNode,
  ListEndpointPaginationLinks,
  AnnotatedJsonSchema,
} from '@web-config-app/core';
import {
  AuditLogTable,
  AuditLogTableRef,
} from '../audit-log-table/audit-log-table.component';
import { AuditLogDataDiff } from '../audit-log-data-diff/audit-log-data-diff.component';
import { TextActionButtonIcon } from '../text-action-button-icon/text-action-button-icon.component';
import { EntityListTablePagination } from '../entity-list-table/entity-list-table-pagination/entity-list-table-pagination.component';

export interface AuditLogModalProps {
  entityType: string;
  isError: boolean;
  isEmpty: boolean;
  isLoading: boolean;
  data: AuditLogRow[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  entityTreeNodeMap?: Map<string, TreeNode>;
  schemaForAuditLogLabels?: AnnotatedJsonSchema;
  pagination?: ListEndpointPaginationLinks;
  loadPage?: (url: string | undefined) => void;
}

const NoDataContent = ({ message }: { message: string }) => {
  const { formatMessage } = useIntl();
  return (
    <StackLayout
      orientation="vertical"
      spacing="$one"
      horizontalAlignment="start"
    >
      <HeadingText level="2" size="sm">
        {message}
      </HeadingText>
      <Modal.Close>
        <Button priority="primary" size="medium">
          {formatMessage({ id: 'CLOSE' })}
        </Button>
      </Modal.Close>
    </StackLayout>
  );
};

export const AuditLogModal = ({
  entityType,
  isError,
  isEmpty,
  isLoading,
  data = [],
  isOpen,
  onOpenChange,
  entityTreeNodeMap,
  schemaForAuditLogLabels,
  pagination,
  loadPage,
}: AuditLogModalProps) => {
  const { formatMessage, formatDate } = useIntl();
  const tableRef = React.useRef<AuditLogTableRef | null>(null);
  const handleLoadPage = React.useCallback(
    (url: string) => {
      if (loadPage) {
        loadPage(url);
      }
      tableRef.current?.resetExpanded();
    },
    [loadPage],
  );
  const auditLogColumns: ColumnDef<AuditLogRow>[] = React.useMemo(
    () => [
      {
        accessorKey: 'version',
        header: formatMessage({ id: 'AUDIT_LOG_VERSION' }),
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'date',
        header: formatMessage({ id: 'AUDIT_LOG_DATE' }),
        cell: (info) => {
          const dateValue = info.getValue() as string;
          try {
            // Format date to MM-DD-YYYY using useIntl's formatDate
            return formatDate(new Date(dateValue), {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            }).replace(/\//g, '-');
          } catch (error) {
            // Fallback to original value if date parsing fails
            return dateValue;
          }
        },
      },
      {
        accessorKey: 'user',
        header: formatMessage({ id: 'AUDIT_LOG_USER' }),
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'status',
        header: formatMessage({ id: 'AUDIT_LOG_STATUS' }),
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'actionType',
        header: formatMessage({ id: 'AUDIT_LOG_ACTION_TYPE' }),
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'numberOfChanges',
        header: formatMessage({ id: 'AUDIT_LOG_NUMBER_OF_CHANGES' }),
        cell: (info) => info.getValue(),
      },
    ],
    [formatMessage, formatDate],
  );

  // The contentDiff is calculated in transformAuditLogData() and included on each AuditLogRow
  const renderExpandedContent = React.useCallback(
    (row: AuditLogRow) => {
      if (!row.contentDiff || row.contentDiff.length === 0) {
        return (
          <ParagraphText size="sm">
            {formatMessage({ id: 'NO_CHANGES_DETECTED' })}
          </ParagraphText>
        );
      }
      return (
        <AuditLogDataDiff
          data={row.contentDiff}
          entityVersion={row.version.toString()}
          previousEntityVersion={row.fromEntity?.attributes?.entityMetadata?.version?.toString()}
          isLoading={false}
          entityTreeNodeMap={entityTreeNodeMap}
          schemaForAuditLogLabels={schemaForAuditLogLabels}
          fromEntity={row.fromEntity}
          toEntity={row.toEntity}
        />
      );
    },
    [schemaForAuditLogLabels, entityTreeNodeMap, formatMessage],
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={onOpenChange}>
      <Modal.Trigger>
        <TextActionButtonIcon
          icon="descriptivePaymentCard"
          label={formatMessage({ id: 'AUDIT_LOG' })}
          size="1.25rem"
        />
      </Modal.Trigger>
      <Modal.Content layout="fullscreen" showCloseButton={false}>
        <StackLayout
          orientation="vertical"
          horizontalAlignment="stretch"
          css={{ height: '100%' }}
        >
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="spaceBetween"
            css={{ flexGrow: 0 }}
          >
            <Modal.Title>
              <HeadingText level="1" size="xl">
                {formatMessage({ id: 'AUDIT_LOG' })}
              </HeadingText>
            </Modal.Title>
            <Modal.Close>
              <Button
                priority="tertiary"
                quiet
                icon="tinyClose"
                hideLabel
                size="small"
                className="GDS-modal-close-button"
              >
                {formatMessage({ id: 'CLOSE' })}
              </Button>
            </Modal.Close>
          </StackLayout>
          <Divider />

          <Box>
            {isError && (
              <NoDataContent
                message={formatMessage({ id: 'NO_AUDIT_LOG_ERROR' })}
              />
            )}
            {isEmpty && (
              <NoDataContent
                message={formatMessage({ id: 'NO_AUDIT_LOG_EMPTY' })}
              />
            )}

            {!isError && !isEmpty && (
              <StackLayout
                orientation="vertical"
                spacing="$one"
                horizontalAlignment="stretch"
                css={{ height: '100%' }}
              >
                <Modal.Description>
                  <Box>
                    <HeadingText
                      level="2"
                      size="sm"
                      css={{ fontWeight: 'normal' }}
                    >
                      {entityType}
                    </HeadingText>
                  </Box>
                </Modal.Description>
                <AuditLogTable
                  ref={tableRef}
                  isLoading={isLoading}
                  data={data}
                  columns={auditLogColumns}
                  renderExpandedContent={renderExpandedContent}
                />
                {pagination && loadPage && (
                  <EntityListTablePagination
                    pagination={pagination}
                    loadPage={handleLoadPage}
                  />
                )}
              </StackLayout>
            )}
          </Box>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
